import React, { useState } from "react";
import { Form, Modal, Button } from "react-bootstrap";
import axios from "axios";
import { BASE_URL } from "../../../env";

/**
 * Step 4 of the Daily Loan Branch Manager disbursement flow.
 *
 * ONE mandatory proof image is required before the Disburse button appears:
 *   "document" – uploaded to POST /api/uploadDlDisbursementPhoto, stored in
 *                member_details.dlSignedDocumentPhoto
 *
 * This modal used to ask for a second "cashHandover" photo as well. The client
 * asked for a single "Attach proof image" field instead, so only the one above
 * is collected. The dlCashHandoverPhoto column is intentionally left in the
 * table - loans disbursed before the change hold real filenames there - but
 * nothing reads or writes it now, and confirmDlDisbursement.js no longer
 * requires it.
 */
const DlProofPhotosModal = ({
  show,
  handleClose,
  memberId,
  onSuccess,
  // Pass the current DB value so we can show whether it is already saved
  dlSignedDocumentPhoto,
}) => {
  const [docFile, setDocFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const uploadOne = async (file, photoType) => {
    const formData = new FormData();
    formData.append("memberId", memberId);
    formData.append("photoType", photoType);
    formData.append("photo", file);
    await axios.post(`${BASE_URL}/api/uploadDlDisbursementPhoto`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const handleConfirm = async () => {
    setError("");

    // Mandatory: refuse to continue without an image, unless one is already
    // saved and the BM opened the modal without picking a replacement.
    if (!docFile) {
      setError(
        dlSignedDocumentPhoto
          ? "Select a new image to replace the saved one, or press Cancel."
          : "Proof image is required. Please select an image."
      );
      return;
    }

    setLoading(true);
    try {
      await uploadOne(docFile, "document");

      setDocFile(null);
      handleClose();
      onSuccess();
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.details ||
        "Failed to upload photo(s). Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Daily Loan — Attach Proof Image</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger">{error}</div>}
        <p className="text-muted mb-3">
          The proof image must be uploaded before the Disburse button appears.
        </p>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              Attach Proof Image <span className="text-danger">*</span>{" "}
              {dlSignedDocumentPhoto && (
                <span className="text-success">✓ Already uploaded</span>
              )}
            </Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              required
              onChange={(e) => setDocFile(e.target.files[0] || null)}
            />
            <Form.Text className="text-muted">
              Clear photo of the customer holding or signing the agreement.
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={loading}>
          {loading ? "Uploading…" : "Upload Proof Image"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DlProofPhotosModal;
